import { useEffect, useMemo, useState } from 'react';

import { apiFetch, apiFetchText, ApiError } from '../../api/client';
import { useCohort, type CohortItem } from '../../hooks/usePipelineInsights';
import {
  type PromptPurpose,
  useActivatePromptVersion,
  useCreatePromptVersion,
  usePromptDetail,
  usePromptDiff,
  useCorrectionsSummary,
  usePromptList,
} from '../../hooks/usePrompts';
import styles from './PromptsPage.module.css';

const PURPOSES: { key: PromptPurpose; label: string }[] = [
  { key: 'aggregator', label: 'Aggregator' },
  // WP3/S3.3 — the exit (sell-side) aggregator prompt. Beside 'Aggregator' because they are the
  // same seat for two kinds of deal, and because this tab is the ONLY place a composed exit
  // candidate can be read and activated.
  { key: 'exit_aggregator', label: 'Aggregator · Exit' },
  { key: 'classifier', label: 'Classifier' },
  { key: 'nda_review', label: 'NDA Review Policy' },
  { key: 'crm_enrich_triage', label: 'CRM Enrich · Triage' },
  { key: 'crm_enrich_reconcile', label: 'CRM Enrich · Reconcile' },
  { key: 'crm_enrich_orgname', label: 'CRM Enrich · Org Name' },
  { key: 'crm_enrich_deepdive', label: 'CRM Enrich · Deep Dive' },
];

type ViewMode = 'detail' | 'diff';
type TopMode = 'versions' | 'preview' | 'corrections';

// Pipeline Insights → Prompts deep-link context (PR 4). When the
// operator clicks "Open <foo> prompt" on a Health-mode issue, the URL
// carries `from_pi=<issue.kind>&purpose=<aggregator|classifier>&...`.
// We surface this as an impact-context strip at the top of the
// preview panel and auto-load a representative case from the matching
// cohort so the operator can see the broken output before editing.
export interface ImpactContext {
  fromPi: string;            // issue.kind: materiality, field_override, etc.
  purpose: PromptPurpose;
  source?: string;
  fieldName?: string;
  days?: number;
}

// Maps a from_pi issue kind to the cohort name we should sample for
// an auto-load case. Returns null when no cohort is suitable (e.g.
// aggregator_health — operator picks a deal manually).
function cohortForIssue(fromPi: string): string | null {
  switch (fromPi) {
    case 'materiality':       return 'materiality_high';
    case 'field_override':    return 'field_override';
    case 'resolver_no_match': return 'resolver_no_match';
    case 'frequent_unbinds':  return 'frequent_unbinds';
    default:                  return null;
  }
}

function humanIssueLabel(fromPi: string): string {
  switch (fromPi) {
    case 'materiality':       return 'Materiality calibration';
    case 'field_override':    return 'Field override spike';
    case 'resolver_no_match': return 'Resolver no-match rate';
    case 'frequent_unbinds':  return 'Frequent unbinds';
    case 'aggregator_health': return 'Aggregator health';
    case 'push_failures':     return 'Push failures';
    default:                  return fromPi;
  }
}

/** E-15 — the corrections readout: how often operators overrode each prompt version's output.
 * Unions the two correction ledgers that carry prompt lineage (enrichment field Reject/Edit +
 * IOT field-review feedback); Phase B dismissals stay in the Learning Portal (no per-row lineage). */
function CorrectionsPanel() {
  const [days, setDays] = useState(90);
  const q = useCorrectionsSummary(days);
  const d = q.data;
  return (
    <div className={styles.correctionsPanel}>
      <div className={styles.correctionsHeader}>
        <label>
          Window{' '}
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>365 days</option>
          </select>
        </label>
        {d && (
          <span className={styles.correctionsSources}>
            {Object.entries(d.sources).map(([k, v]) => `${k}: ${v}`).join(' · ')}
          </span>
        )}
      </div>
      {q.isLoading && <div className={styles.emptyHint}>Loading…</div>}
      {q.error != null && <div className={styles.emptyHint}>Failed to load the corrections summary.</div>}
      {d && d.versions.length === 0 && (
        <div className={styles.emptyHint}>No corrections recorded in this window.</div>
      )}
      {d && d.versions.length > 0 && (
        <table className={styles.correctionsTable}>
          <thead>
            <tr>
              <th>Prompt version</th>
              <th>Accepts</th>
              <th>Edits</th>
              <th>Rejects</th>
              <th>Correction rate</th>
              <th>Reject reasons</th>
              <th>Most-corrected fields</th>
            </tr>
          </thead>
          <tbody>
            {d.versions.map((v) => (
              <tr key={v.prompt_version_id ?? 'none'}>
                <td>
                  {v.prompt_version_id === null
                    ? 'unattributed'
                    : `${v.purpose ?? '?'} / ${v.version_tag ?? v.prompt_version_id}`}
                  {v.is_active && <span className={styles.correctionsActive}> · active</span>}
                </td>
                <td>{v.accepts}</td>
                <td>{v.edit_accepts}</td>
                <td>{v.rejects}</td>
                <td>{(v.correction_rate * 100).toFixed(1)}%</td>
                <td>{Object.entries(v.reject_reasons).map(([k, n]) => `${k} (${n})`).join(', ') || '—'}</td>
                <td>{Object.entries(v.top_fields).map(([k, n]) => `${k} (${n})`).join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function PromptsPage() {
  // Read the optional `?invocation=<id>` query param so links from
  // Trace tab + signal/suggestion rows deep-link straight into the
  // historical-invocation preview.
  const initialInvocationId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const v = new URLSearchParams(window.location.search).get('invocation');
    return v && v.trim() ? v.trim() : null;
  }, []);
  // PR 4 — Pipeline Insights deep-link context.
  const impactContext = useMemo<ImpactContext | null>(() => {
    if (typeof window === 'undefined') return null;
    const q = new URLSearchParams(window.location.search);
    const fromPi = q.get('from_pi');
    const purposeParam = q.get('purpose');
    if (!fromPi || !purposeParam) return null;
    if (purposeParam !== 'aggregator' && purposeParam !== 'classifier') return null;
    return {
      fromPi,
      purpose: purposeParam as PromptPurpose,
      source: q.get('source') || undefined,
      fieldName: q.get('field_name') || undefined,
      days: q.get('days') ? parseInt(q.get('days')!, 10) : undefined,
    };
  }, []);
  const [topMode, setTopMode] = useState<TopMode>(
    initialInvocationId || impactContext ? 'preview' : 'versions',
  );
  const [purpose, setPurpose] = useState<PromptPurpose>(
    impactContext?.purpose ?? 'aggregator',
  );
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('detail');
  const [diffAgainstId, setDiffAgainstId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const list = usePromptList(purpose);
  const versions = list.data ?? [];

  // Auto-select the active version (or the newest) when the list arrives or
  // the purpose changes.
  useEffect(() => {
    if (!versions.length) {
      setSelectedId(null);
      return;
    }
    const active = versions.find((v) => v.is_active);
    setSelectedId(active?.id ?? versions[0].id);
  }, [purpose, versions]);

  // Reset diff state when purpose changes
  useEffect(() => {
    setDiffAgainstId(null);
    setViewMode('detail');
  }, [purpose]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>Prompts</h1>
        <span className={styles.subtitle}>
          Version-controlled prompt library — admin only
        </span>
      </div>

      <div className={styles.purposeBar}>
        {PURPOSES.map((p) => (
          <button
            key={p.key}
            className={`${styles.purposeTab} ${
              topMode === 'versions' && purpose === p.key ? styles.purposeTabActive : ''
            }`}
            onClick={() => {
              setTopMode('versions');
              setPurpose(p.key);
            }}
          >
            {p.label}
          </button>
        ))}
        <button
          className={`${styles.purposeTab} ${
            topMode === 'preview' ? styles.purposeTabActive : ''
          }`}
          onClick={() => setTopMode('preview')}
        >
          Live preview
        </button>
        <button
          className={`${styles.purposeTab} ${
            topMode === 'corrections' ? styles.purposeTabActive : ''
          }`}
          onClick={() => setTopMode('corrections')}
        >
          Corrections
        </button>
        <span className={styles.flexSpacer} />
        {topMode === 'versions' && (
          <button
            className={styles.newButton}
            onClick={() => setShowCreate(true)}
          >
            + New version
          </button>
        )}
      </div>

      {topMode === 'corrections' && <CorrectionsPanel />}
      {topMode === 'preview' && (
        <LivePreviewPanel
          initialInvocationId={initialInvocationId}
          impactContext={impactContext}
          onCreateTaggedVersion={() => setShowCreate(true)}
        />
      )}
      {topMode === 'versions' && (

      <div className={styles.body}>
        <aside className={styles.versionList}>
          <div className={styles.versionListHeader}>
            {list.isLoading ? 'Loading…' : `${versions.length} version${versions.length === 1 ? '' : 's'}`}
          </div>
          {versions.map((v) => (
            <button
              key={v.id}
              className={`${styles.versionRow} ${selectedId === v.id ? styles.versionRowSelected : ''}`}
              onClick={() => {
                setSelectedId(v.id);
                if (viewMode === 'diff' && diffAgainstId === v.id) {
                  setDiffAgainstId(null);
                  setViewMode('detail');
                }
              }}
            >
              <div className={styles.versionRowTop}>
                <span className={styles.versionTag}>{v.version_tag}</span>
                {v.is_active && <span className={styles.activeBadge}>active</span>}
              </div>
              <div className={styles.versionRowMeta}>
                {formatRowDate(v.created_at)} · {v.created_by.split('@')[0]}
              </div>
              {v.notes && (
                <div className={styles.versionRowNotes}>{v.notes}</div>
              )}
            </button>
          ))}
        </aside>

        <section className={styles.viewer}>
          {selectedId === null ? (
            <div className={styles.emptyHint}>
              {list.isLoading ? 'Loading versions…' : 'No versions for this purpose yet.'}
            </div>
          ) : viewMode === 'detail' ? (
            <PromptDetailView
              versionId={selectedId}
              versions={versions}
              diffAgainstId={diffAgainstId}
              onDiffAgainstChange={(id) => {
                setDiffAgainstId(id);
                setViewMode(id !== null ? 'diff' : 'detail');
              }}
              onActivated={() => {
                list.refetch();
              }}
            />
          ) : (
            <PromptDiffView
              aId={diffAgainstId!}
              bId={selectedId}
              aTag={versions.find((v) => v.id === diffAgainstId)?.version_tag}
              bTag={versions.find((v) => v.id === selectedId)?.version_tag}
              onClose={() => {
                setDiffAgainstId(null);
                setViewMode('detail');
              }}
            />
          )}
        </section>
      </div>
      )}

      {showCreate && (
        <CreateVersionModal
          purpose={purpose}
          baseVersionId={selectedId}
          impactContext={impactContext}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            list.refetch();
          }}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Detail view (single version)
// ===========================================================================

interface PromptDetailViewProps {
  versionId: number;
  versions: Array<{ id: number; version_tag: string }>;
  diffAgainstId: number | null;
  onDiffAgainstChange: (id: number | null) => void;
  onActivated: () => void;
}

function PromptDetailView({
  versionId,
  versions,
  diffAgainstId,
  onDiffAgainstChange,
  onActivated,
}: PromptDetailViewProps) {
  const detail = usePromptDetail(versionId);
  const activate = useActivatePromptVersion();
  const v = detail.data;

  if (detail.isLoading || !v) {
    return <div className={styles.emptyHint}>Loading…</div>;
  }

  return (
    <div>
      <div className={styles.viewerHeader}>
        <div>
          <div className={styles.viewerTitle}>
            {v.purpose} · {v.version_tag}
            {v.is_active && <span className={styles.activeBadge} style={{ marginLeft: 8 }}>active</span>}
          </div>
          <div className={styles.viewerSubtitle}>
            Created {formatLongDate(v.created_at)} by {v.created_by}
          </div>
        </div>
        <div className={styles.viewerActions}>
          <label className={styles.diffLabel}>
            Compare to
            <select
              className={styles.select}
              value={diffAgainstId ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                onDiffAgainstChange(val ? parseInt(val, 10) : null);
              }}
            >
              <option value="">— pick a version —</option>
              {versions
                .filter((other) => other.id !== versionId)
                .map((other) => (
                  <option key={other.id} value={other.id}>{other.version_tag}</option>
                ))}
            </select>
          </label>
          {!v.is_active && (
            <button
              className={styles.activateButton}
              disabled={activate.isPending}
              onClick={() => {
                if (window.confirm(
                  `Activate ${v.purpose} v${v.version_tag}? `
                  + `The current active version will be deactivated.`,
                )) {
                  activate.mutate(v.id, { onSuccess: onActivated });
                }
              }}
            >
              {activate.isPending ? 'Activating…' : 'Activate'}
            </button>
          )}
        </div>
      </div>

      {v.notes && (
        <div className={styles.notesBox}>{v.notes}</div>
      )}

      <pre className={styles.promptBody}>{v.system_prompt}</pre>

      {v.user_prompt_template && (
        <>
          <div className={styles.bodyLabel}>User prompt template</div>
          <pre className={styles.promptBody}>{v.user_prompt_template}</pre>
        </>
      )}

      {v.tool_definitions_json && (
        <>
          <div className={styles.bodyLabel}>Tool definitions</div>
          <pre className={styles.promptBody}>{v.tool_definitions_json}</pre>
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Diff view
// ===========================================================================

interface PromptDiffViewProps {
  aId: number;
  bId: number;
  aTag?: string;
  bTag?: string;
  onClose: () => void;
}

function PromptDiffView({ aId, bId, aTag, bTag, onClose }: PromptDiffViewProps) {
  const diff = usePromptDiff(aId, bId);

  return (
    <div>
      <div className={styles.viewerHeader}>
        <div className={styles.viewerTitle}>
          Diff: {aTag ?? `#${aId}`} → {bTag ?? `#${bId}`}
        </div>
        <button className={styles.closeButton} onClick={onClose}>
          ✕ Back to detail
        </button>
      </div>
      {diff.isLoading && <div className={styles.emptyHint}>Loading diff…</div>}
      {diff.data && (
        diff.data.unified_diff.trim() === '' ? (
          <div className={styles.emptyHint}>
            (no differences in system_prompt)
          </div>
        ) : (
          <pre className={styles.diffBody}>
            {diff.data.unified_diff.split('\n').map((line, i) => (
              <span key={i} className={diffLineClass(line)}>
                {line + '\n'}
              </span>
            ))}
          </pre>
        )
      )}
    </div>
  );
}

function diffLineClass(line: string): string {
  if (line.startsWith('+++') || line.startsWith('---')) return styles.diffHeader;
  if (line.startsWith('@@')) return styles.diffHunk;
  if (line.startsWith('+')) return styles.diffAdd;
  if (line.startsWith('-')) return styles.diffRemove;
  return styles.diffContext;
}

// ===========================================================================
// Create new version modal
// ===========================================================================

interface CreateModalProps {
  purpose: PromptPurpose;
  baseVersionId: number | null;
  impactContext?: ImpactContext | null;
  onClose: () => void;
  onCreated: () => void;
}

function CreateVersionModal({
  purpose,
  baseVersionId,
  impactContext,
  onClose,
  onCreated,
}: CreateModalProps) {
  const baseDetail = usePromptDetail(baseVersionId);
  const create = useCreatePromptVersion();
  const [tag, setTag] = useState('');
  const [body, setBody] = useState('');
  // PR 4 — when arriving from Pipeline Insights, stamp the new
  // version with the source-issue token so prompt history is
  // queryable by "what investigation drove this change".
  const defaultNotes = useMemo(() => {
    if (!impactContext) return '';
    const parts = [`source_issue: ${impactContext.fromPi}`];
    if (impactContext.source) parts.push(`source: ${impactContext.source}`);
    if (impactContext.fieldName) parts.push(`field: ${impactContext.fieldName}`);
    return parts.join(' ');
  }, [impactContext]);
  const [notes, setNotes] = useState(defaultNotes);
  const [activate, setActivate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill the body from the currently-selected version
  const prefilled = useMemo(() => baseDetail.data?.system_prompt ?? '', [baseDetail.data]);
  useEffect(() => {
    if (prefilled && !body) {
      setBody(prefilled);
    }
  }, [prefilled, body]);

  const onSubmit = () => {
    setError(null);
    if (!tag.trim()) {
      setError('Version tag is required.');
      return;
    }
    if (!body.trim()) {
      setError('System prompt body is required.');
      return;
    }
    create.mutate(
      {
        purpose,
        version_tag: tag.trim(),
        system_prompt: body,
        notes: notes.trim() || null,
        activate_immediately: activate,
      },
      {
        onSuccess: onCreated,
        onError: (e) => {
          setError(e.message || 'Failed to create version.');
        },
      },
    );
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>New {purpose} version</h2>
          <button className={styles.closeButton} onClick={onClose}>✕</button>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Version tag (e.g. 1.3.0)</span>
          <input
            className={styles.input}
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            placeholder="1.3.0"
            autoFocus
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Notes (optional)</span>
          <input
            className={styles.input}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What did this version change?"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>System prompt</span>
          <textarea
            className={styles.textarea}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={20}
            placeholder="Prompt body…"
          />
        </label>

        <label className={styles.checkboxField}>
          <input
            type="checkbox"
            checked={activate}
            onChange={(e) => setActivate(e.target.checked)}
          />
          <span>
            Activate immediately
            <span className={styles.checkboxHint}>
              {' '}— deactivates the current active version
            </span>
          </span>
        </label>

        {error && <div className={styles.errorBox}>{error}</div>}

        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>Cancel</button>
          <button
            className={styles.submitButton}
            onClick={onSubmit}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create version'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function formatRowDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatLongDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}


/* ===========================================================================
 * Live preview panel — renders the prompts that Phase 3a flags will produce
 * BEFORE they're flipped, plus a lookup for any historical prompt_invocation
 * by id (used by Trace tab links).
 * =========================================================================== */

type PreviewKind = 'blocks' | 'aggregator' | 'classifier' | 'postprocessor' | 'invocation';

const PREVIEW_OPTIONS: Array<{ key: PreviewKind; label: string; help: string }> = [
  {
    key: 'blocks',
    label: 'ContextKit blocks',
    help: 'Render every ContextKit baseline block (vocab, pipeline_state, portfolio).',
  },
  {
    key: 'aggregator',
    label: 'Aggregator (3a-sync)',
    help: 'Full aggregator user prompt with the 5 ContextKit blocks prepended (preview of AGGREGATOR_VOCAB_INJECTION_ENABLED).',
  },
  {
    key: 'classifier',
    label: 'Classifier (3a-classifier)',
    help: 'Classifier user prompt with the PIPELINE_STATE block + redirect_proposal instruction prepended (preview of CLASSIFIER_PIPELINE_CONTEXT_ENABLED).',
  },
  {
    key: 'postprocessor',
    label: 'Org-enrichment postprocessor (3a-postprocessor)',
    help: 'Full org-enrichment user prompt for one deal (preview of ORG_ENRICHMENT_POSTPROCESSOR_ENABLED).',
  },
  {
    key: 'invocation',
    label: 'Historical invocation (Trace lookup)',
    help: 'Fetch the exact rendered system + user prompt + raw response for any prior prompt_invocation_id (linked from Trace tab + signal/suggestion rows).',
  },
];


interface LivePreviewPanelProps {
  // When supplied (deep-link from Trace tab via `?invocation=<id>`),
  // the panel opens on the `invocation` mode with the id pre-filled
  // and auto-renders so the operator sees the rendered prompt
  // immediately. Pure presentational handoff — the parent reads the
  // URL once on mount.
  initialInvocationId?: string | null;
  // PR 4 — when arriving from a Pipeline Insights Health-mode
  // chip, we render an impact-context strip above the controls
  // explaining what's broken and auto-load a representative case
  // from the matching cohort.
  impactContext?: ImpactContext | null;
  onCreateTaggedVersion?: () => void;
}


function LivePreviewPanel({
  initialInvocationId,
  impactContext,
  onCreateTaggedVersion,
}: LivePreviewPanelProps) {
  // Initial kind: invocation overrides everything (Trace deep-link);
  // else match the impactContext purpose (Pipeline Insights deep-link);
  // else default to blocks (manual exploration).
  const initialKind: PreviewKind = initialInvocationId
    ? 'invocation'
    : impactContext?.purpose === 'classifier'
      ? 'classifier'
      : impactContext?.purpose === 'aggregator'
        ? 'aggregator'
        : 'blocks';
  const [kind, setKind] = useState<PreviewKind>(initialKind);
  const [dealKind, setDealKind] = useState<string>('candidate');
  const [dealKey, setDealKey] = useState<string>('');
  const [signalId, setSignalId] = useState<string>('');
  const [invocationId, setInvocationId] = useState<string>(
    initialInvocationId ?? '',
  );
  const [output, setOutput] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track whether we've already auto-rendered for the deep-link so
  // we don't re-fire on every state change.
  const [autoRan, setAutoRan] = useState(false);

  // PR 4 — fetch one representative case from the relevant cohort.
  // Limit=1 keeps the round-trip cheap; the operator can still pick
  // a different signal/deal manually after auto-render.
  const cohortKind = impactContext ? cohortForIssue(impactContext.fromPi) : null;
  const cohortParams: Record<string, string | number | undefined> = {};
  if (impactContext?.fieldName) cohortParams.field_name = impactContext.fieldName;
  if (impactContext?.days) cohortParams.days = impactContext.days;
  const cohortQuery = useCohort(cohortKind, cohortParams);
  const sampleCase: CohortItem | null = cohortQuery.data?.items?.[0] ?? null;

  // Auto-load the sample case into the appropriate input field once
  // it arrives. Only fires if the operator hasn't already typed in.
  useEffect(() => {
    if (!sampleCase) return;
    if (sampleCase.signal_id && !signalId) {
      setSignalId(String(sampleCase.signal_id));
    }
    if (sampleCase.deal_key && !dealKey) {
      setDealKey(sampleCase.deal_key);
      if (sampleCase.deal_kind) {
        setDealKind(sampleCase.deal_kind);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleCase]);

  const onRender = async () => {
    setLoading(true);
    setError(null);
    setOutput('');
    try {
      let url = '';
      switch (kind) {
        case 'blocks':
          url = '/api/admin/prompt-preview/blocks';
          if (dealKey.trim()) {
            const params = new URLSearchParams({
              deal_kind: dealKind,
              deal_key: dealKey.trim(),
            });
            url += `?${params.toString()}`;
          }
          break;
        case 'aggregator': {
          if (!dealKey.trim()) {
            throw new Error('deal_key required for aggregator preview');
          }
          const params = new URLSearchParams({
            deal_kind: dealKind,
            deal_key: dealKey.trim(),
          });
          url = `/api/admin/prompt-preview/aggregator?${params.toString()}`;
          break;
        }
        case 'classifier':
          url = '/api/admin/prompt-preview/classifier';
          if (signalId.trim()) {
            url += `?signal_id=${encodeURIComponent(signalId.trim())}`;
          }
          break;
        case 'postprocessor': {
          if (!dealKey.trim()) {
            throw new Error('deal_key required for postprocessor preview');
          }
          const params = new URLSearchParams({
            deal_kind: dealKind,
            deal_key: dealKey.trim(),
          });
          url = `/api/admin/prompt-preview/postprocessor?${params.toString()}`;
          break;
        }
        case 'invocation': {
          if (!invocationId.trim()) {
            throw new Error('invocation_id required');
          }
          const detail = await apiFetch<{
            id: number;
            purpose: string;
            model: string;
            invoked_at: string;
            input_tokens: number | null;
            output_tokens: number | null;
            cost_usd: number | null;
            rendered_system: string;
            rendered_user: string;
            raw_response: string | null;
          }>(`/api/admin/prompts/invocations/${encodeURIComponent(invocationId.trim())}`);
          const text = (
            `# PROMPT INVOCATION #${detail.id}\n`
            + `# purpose=${detail.purpose} model=${detail.model}\n`
            + `# invoked_at=${detail.invoked_at}\n`
            + `# tokens: in=${detail.input_tokens ?? '?'} out=${detail.output_tokens ?? '?'}`
            + ` cost=$${detail.cost_usd ?? '?'}\n`
            + `\n=== SYSTEM PROMPT (as actually sent) ===\n\n`
            + `${detail.rendered_system}\n\n`
            + `=== USER PROMPT (as actually sent) ===\n\n`
            + `${detail.rendered_user}\n\n`
            + `=== RAW RESPONSE ===\n\n`
            + `${detail.raw_response ?? '(none persisted)'}\n`
          );
          setOutput(text);
          setLoading(false);
          return;
        }
      }
      const text = await apiFetchText(url);
      setOutput(text);
    } catch (e) {
      const msg = e instanceof ApiError
        ? `${e.message}\n\n${typeof e.detail === 'string' ? e.detail : JSON.stringify(e.detail)}`
        : (e as Error).message || String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Deep-link auto-render. Two trigger paths:
  //   1. Trace tab: ?invocation=<id> → fire as soon as we mount.
  //   2. Pipeline Insights: ?from_pi=...&purpose=... → wait for the
  //      sample case to land in signalId/dealKey, then fire.
  // Both gated by `autoRan` so we don't re-fire on later edits.
  useEffect(() => {
    if (autoRan) return;
    if (initialInvocationId) {
      setAutoRan(true);
      onRender();
      return;
    }
    if (impactContext) {
      const ready =
        (impactContext.purpose === 'classifier' && signalId)
        || (impactContext.purpose === 'aggregator' && dealKey);
      if (ready) {
        setAutoRan(true);
        onRender();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInvocationId, impactContext, signalId, dealKey, autoRan]);

  return (
    <div className={styles.previewPanel}>
      {impactContext && (
        <div className={styles.impactStrip}>
          <div className={styles.impactStripHeader}>
            <span className={styles.impactStripChip}>↳ Pipeline Insights</span>
            <span className={styles.impactStripTitle}>
              Investigating: {humanIssueLabel(impactContext.fromPi)}
            </span>
          </div>
          <div className={styles.impactStripMeta}>
            {impactContext.source && (
              <span className={styles.impactStripMetaItem}>
                source: <code>{impactContext.source}</code>
              </span>
            )}
            {impactContext.fieldName && (
              <span className={styles.impactStripMetaItem}>
                field: <code>{impactContext.fieldName}</code>
              </span>
            )}
            {impactContext.days && (
              <span className={styles.impactStripMetaItem}>
                window: last {impactContext.days}d
              </span>
            )}
            <span className={styles.impactStripMetaItem}>
              purpose: <code>{impactContext.purpose}</code>
            </span>
          </div>
          {cohortKind && (
            <div className={styles.impactStripCase}>
              {cohortQuery.isLoading && 'Loading representative case…'}
              {cohortQuery.error && (
                <span className={styles.impactStripError}>
                  Could not load case from cohort {cohortKind}.
                </span>
              )}
              {sampleCase && (
                <>
                  Auto-loaded case:{' '}
                  {sampleCase.signal_id && (
                    <code>signal #{sampleCase.signal_id}</code>
                  )}
                  {sampleCase.deal_key && (
                    <code>
                      {sampleCase.deal_kind}/{sampleCase.deal_key}
                    </code>
                  )}
                  {sampleCase.subject && (
                    <span className={styles.impactStripSubject}>
                      {' '}
                      — "{sampleCase.subject}"
                    </span>
                  )}
                </>
              )}
              {!cohortQuery.isLoading && !sampleCase && !cohortQuery.error && (
                <span className={styles.impactStripEmpty}>
                  No recent cases in cohort {cohortKind}. Pick a case manually below.
                </span>
              )}
            </div>
          )}
          {onCreateTaggedVersion && (
            <div className={styles.impactStripActions}>
              <button
                type="button"
                className={styles.impactStripBtn}
                onClick={onCreateTaggedVersion}
              >
                + New version tagged for this issue
              </button>
            </div>
          )}
        </div>
      )}
      <div className={styles.previewControls}>
        <div className={styles.previewKindRow}>
          {PREVIEW_OPTIONS.map((opt) => (
            <label key={opt.key} className={styles.previewKindOption}>
              <input
                type="radio"
                name="preview-kind"
                value={opt.key}
                checked={kind === opt.key}
                onChange={() => {
                  setKind(opt.key);
                  setOutput('');
                  setError(null);
                }}
              />
              <span className={styles.previewKindLabel}>{opt.label}</span>
            </label>
          ))}
        </div>
        <div className={styles.previewHelp}>
          {PREVIEW_OPTIONS.find((o) => o.key === kind)?.help}
        </div>

        <div className={styles.previewInputs}>
          {(kind === 'blocks' || kind === 'aggregator' || kind === 'postprocessor') && (
            <>
              <label className={styles.previewInputLabel}>
                deal_kind
                <select
                  value={dealKind}
                  onChange={(e) => setDealKind(e.target.value)}
                  className={styles.previewSelect}
                >
                  <option value="candidate">candidate</option>
                  <option value="affinity_opp">affinity_opp</option>
                </select>
              </label>
              <label className={styles.previewInputLabel}>
                deal_key
                {kind !== 'blocks' && <span className={styles.previewRequired}>*</span>}
                <input
                  type="text"
                  value={dealKey}
                  onChange={(e) => setDealKey(e.target.value)}
                  placeholder={
                    dealKind === 'affinity_opp'
                      ? 'e.g. 101504817'
                      : 'e.g. cand-abc-uuid'
                  }
                  className={styles.previewInput}
                />
              </label>
            </>
          )}
          {kind === 'classifier' && (
            <label className={styles.previewInputLabel}>
              signal_id (optional)
              <input
                type="text"
                value={signalId}
                onChange={(e) => setSignalId(e.target.value)}
                placeholder="leave blank for synthetic placeholder"
                className={styles.previewInput}
              />
            </label>
          )}
          {kind === 'invocation' && (
            <label className={styles.previewInputLabel}>
              invocation_id <span className={styles.previewRequired}>*</span>
              <input
                type="text"
                value={invocationId}
                onChange={(e) => setInvocationId(e.target.value)}
                placeholder="from Trace tab or signal row"
                className={styles.previewInput}
              />
            </label>
          )}
          <button
            type="button"
            className={styles.previewRenderBtn}
            onClick={onRender}
            disabled={loading}
          >
            {loading ? 'Rendering…' : 'Render'}
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.previewError}>{error}</div>
      )}
      {output && (
        <pre className={styles.previewOutput}>{output}</pre>
      )}
      {!output && !error && !loading && (
        <div className={styles.previewPlaceholder}>
          Pick a preview type, fill any required fields, and click Render.
        </div>
      )}
    </div>
  );
}
